// Login.tsx — strona logowania admina (F2 + F6 2FA gate).
//
// F6 state machine:
//   idle → submitting → {success | require2fa | requireSetup | error}
//
// - success: api.post('/auth/admin/login') zwrocil {ok:true, admin} → refresh + navigate dashboard.
// - require2fa: {require2fa, challengeToken} → pokaz form TOTP code → submit do /login/2fa.
// - requireSetup: {requireSetup, setupToken} → navigate('/admin/2fa/setup', { state: { setupToken } }).
// - error: 401 INVALID_CREDENTIALS albo inny — pokaz message.
//
// Po udanym /login/2fa: api zwraca {ok:true, admin} + Set-Cookie → refresh + navigate.

import { type FormEvent, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useNavigate } from 'react-router-dom';

import { Button } from '@/components/Button';
import { Input } from '@/components/Input';
import { useAuth } from '@/contexts/AuthContext';
import { api } from '@/lib/api';

type State =
  | { kind: 'idle' }
  | { kind: 'submitting' }
  | { kind: 'require2fa'; challengeToken: string }
  | { kind: 'submitting2fa'; challengeToken: string }
  | { kind: 'error'; message: string };

interface LoginResponse {
  ok?: true;
  admin?: { id: string; email: string; name: string | null; role: string };
  requireSetup?: true;
  setupToken?: string;
  require2fa?: true;
  challengeToken?: string;
  expiresIn?: number;
}

interface Login2FAResponse {
  ok: true;
  admin: { id: string; email: string; name: string | null; role: string };
}

export function Login() {
  const { refresh } = useAuth();
  const { t } = useTranslation();
  const navigate = useNavigate();

  const [identifier, setIdentifier] = useState('admin@localhost.test');
  const [password, setPassword] = useState('');
  const [totpCode, setTotpCode] = useState('');
  const [state, setState] = useState<State>({ kind: 'idle' });

  const onSubmitPassword = async (e: FormEvent) => {
    e.preventDefault();
    setState({ kind: 'submitting' });
    try {
      const { data } = await api.post<LoginResponse>('/auth/admin/login', {
        identifier,
        password,
      });

      if (data.requireSetup && data.setupToken) {
        // Idz do setup flow — przekazujemy setupToken przez location state
        // (nie query string, bo to sensitive).
        navigate('/admin/2fa/setup', { state: { setupToken: data.setupToken } });
        return;
      }

      if (data.require2fa && data.challengeToken) {
        setState({ kind: 'require2fa', challengeToken: data.challengeToken });
        return;
      }

      if (data.ok && data.admin) {
        await refresh();
        navigate('/dashboard');
        return;
      }

      // Nieznana struktura response.
      setState({ kind: 'error', message: 'Nieoczekiwana odpowiedź serwera' });
    } catch (err) {
      // Axios zmapował 401 na error.
      const msg =
        (err as { response?: { status?: number } })?.response?.status === 401
          ? 'Nieprawidłowy email/login lub hasło'
          : 'Nie udało się zalogować';
      setState({ kind: 'error', message: msg });
      console.error('login failed', err);
    }
  };

  const onSubmitTotp = async (e: FormEvent) => {
    e.preventDefault();
    if (state.kind !== 'require2fa') return;
    const challengeToken = state.challengeToken;

    setState({ kind: 'submitting2fa', challengeToken });
    try {
      await api.post<Login2FAResponse>('/auth/admin/login/2fa', {
        challengeToken,
        totpCode,
      });
      await refresh();
      navigate('/dashboard');
    } catch (err) {
      const status = (err as { response?: { status?: number } })?.response?.status;
      if (status === 401) {
        setState({
          kind: 'require2fa',
          challengeToken,
        });
        // Keep form visible with TOTP input — ale pokaż error.
        // Prostsze: error-stan + retry button.
      }
      setState({
        kind: 'error',
        message:
          status === 401
            ? 'Kod TOTP niepoprawny lub wygasł. Zacznij od nowa.'
            : 'Weryfikacja 2FA nieudana',
      });
      console.error('login/2fa failed', err);
    }
  };

  const onBackToLogin = () => {
    setTotpCode('');
    setState({ kind: 'idle' });
  };

  const loadingPassword = state.kind === 'submitting';
  const loading2fa = state.kind === 'submitting2fa';
  const showTotpForm = state.kind === 'require2fa' || state.kind === 'submitting2fa';
  const errorMessage = state.kind === 'error' ? state.message : null;

  return (
    <main className="flex min-h-screen items-center justify-center bg-bg-secondary p-4">
      <div className="w-full max-w-md rounded-2xl border border-border-secondary bg-bg-elevated p-8 shadow-xl">
        <div className="mb-8 text-center">
          <h1 className="text-2xl font-bold text-text-primary">SaaS v2 — panel admina</h1>
          <p className="mt-2 text-sm text-text-tertiary">
            {showTotpForm
              ? 'Wprowadź kod z aplikacji Authenticator'
              : 'Zaloguj się, aby kontynuować'}
          </p>
        </div>

        {!showTotpForm && (
          <form onSubmit={onSubmitPassword} className="space-y-4">
            <Input
              label="Email lub login"
              type="text"
              autoComplete="username"
              required
              value={identifier}
              onChange={(e) => setIdentifier(e.target.value)}
              placeholder="admin@example.com lub login"
              disabled={loadingPassword}
            />
            <Input
              label="Hasło"
              type="password"
              autoComplete="current-password"
              required
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              disabled={loadingPassword}
            />

            {errorMessage && (
              <div
                role="alert"
                className="rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700 dark:border-red-800 dark:bg-red-900/30 dark:text-red-300"
              >
                {errorMessage}
              </div>
            )}

            <Button type="submit" className="w-full" loading={loadingPassword}>
              {loadingPassword ? 'Logowanie…' : 'Zaloguj się'}
            </Button>
            <div className="text-center">
              <Link
                to="/forgot-password"
                className="text-sm text-text-tertiary hover:text-text-primary"
              >
                {t('admin.login.forgot')}
              </Link>
            </div>
          </form>
        )}

        {showTotpForm && (
          <form onSubmit={onSubmitTotp} className="space-y-4">
            <Input
              label="Kod 6-cyfrowy z Authenticator"
              type="text"
              inputMode="numeric"
              pattern="\d{6}"
              maxLength={6}
              autoComplete="one-time-code"
              required
              value={totpCode}
              onChange={(e) => setTotpCode(e.target.value.replace(/\D/g, ''))}
              placeholder="123456"
              disabled={loading2fa}
            />

            {errorMessage && (
              <div
                role="alert"
                className="rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700 dark:border-red-800 dark:bg-red-900/30 dark:text-red-300"
              >
                {errorMessage}
              </div>
            )}

            <Button type="submit" className="w-full" loading={loading2fa}>
              {loading2fa ? 'Weryfikacja…' : 'Potwierdź'}
            </Button>
            <Button
              type="button"
              variant="ghost"
              className="w-full"
              onClick={onBackToLogin}
              disabled={loading2fa}
            >
              Wróć do logowania
            </Button>
          </form>
        )}

        <div className="mt-6 border-t border-border-secondary pt-6 text-center text-xs text-text-muted">
          Faza 6 · 2FA · super-admin only
        </div>
      </div>
    </main>
  );
}
